import React from 'react';
import { Box, Grid, Rating, Typography } from '@mui/material';
import { ProfileIconRounded } from 'components/SVG';
import { SparkImageLoader } from 'sharedComponents/UI';
import { formatDateTimeMonth } from 'utilities/time';
import SparkInfiniteScroll from 'sharedComponents/UI/SparkInfiniteScroll';
import { useSparkRatingInfiniteQuery } from 'hooks/spark/useSparkRatingInfiniteQuery';
import SparkMemoryReviewSkeleton from 'sharedComponents/Skeleton/SparkMemoryReviewSkeleton';

type ReviewsPropsType = {
  sparkID: string | undefined;
};

function ReviewsList({ sparkID }: ReviewsPropsType) {
  const {
    sparkRatings: ratings,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSparkRatingInfiniteQuery(sparkID);

  const handleLastElementView = React.useCallback(() => {
    if (!hasNextPage || isLoading || isFetchingNextPage) {
      return;
    }
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]);

  return (
    <Grid container gap={1} paddingRight={1}>
      <SparkInfiniteScroll
        onLastElementView={handleLastElementView}
        skeletonCount={5}
        isLoading={isLoading}
        loadingContent={SparkMemoryReviewSkeleton()}
        isFetchingNextPage={isFetchingNextPage}
        childContainerProps={{
          xs: 12,
          padding: 1,
        }}
        containerStyles={{
          overflowY: 'auto',
        }}
      >
        {ratings?.map((rating, index) => {
          const { profilePictureURL, name, surname } = rating.meta.userRating;
          const {
            rating: reviewerRating,
            modifiedAt,
            comment,
          } = rating.attributes;

          const formattedDate = formatDateTimeMonth(modifiedAt, 'D MMMM YYYY');

          return (
            <Grid
              key={index}
              item
              container
              bgcolor={(theme) => theme.palette.bgGray.main}
              borderRadius='10px'
              padding={2}
              xs={12}
              display={'flex'}
              flexDirection={'column'}
            >
              <Box display='flex' alignItems='center' width='100%'>
                {profilePictureURL ? (
                  <SparkImageLoader
                    src={profilePictureURL}
                    alt={profilePictureURL}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                    }}
                  />
                ) : (
                  <ProfileIconRounded width='40px' height='40px' />
                )}
                <Box pl={2} width='100%'>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#000000',
                      }}
                    >
                      {name} {surname}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                <Typography
                  sx={{
                    color: '#BDBDBD',
                    fontSize: '14px',
                    pt: 0.5,
                  }}
                >
                  <Rating
                    value={reviewerRating}
                    precision={1}
                    max={5}
                    size='small'
                    readOnly={true}
                    sx={{
                      fontSize: '15px',
                    }}
                  />
                </Typography>
                <Typography sx={{ fontSize: '12px' }} ml={1}>
                  {formattedDate}
                </Typography>
              </Box>
              <Box>{comment}</Box>
            </Grid>
          );
        })}
      </SparkInfiniteScroll>
    </Grid>
  );
}

export default ReviewsList;
